import React, {useState, useEffect, useRef} from "react"
import InmuebleCard from "@/components/properties/PropertyCard"
import {CSVPopUpWindow} from "@/components/csv/CSVPopUpWindow"
import Page from "@/layouts/Page"
import {getAssociates} from "@/api/asociados"
import {Box} from "@mui/material"
import ListAltIcon from "@mui/icons-material/ListAlt"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import FormControl from "@mui/material/FormControl"
import Select, {SelectChangeEvent} from "@mui/material/Select"
import Chip from "@mui/material/Chip"
import OutlinedInput from "@mui/material/OutlinedInput"
import {CDBLUE} from "@/constants"
import AddIcon from "@mui/icons-material/Add"
import {useAuthContext} from "@/hooks/useAuthContext"
import {Link, useLocation} from "react-router-dom"
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward"
import {PropertyResponse as Property} from "@/types/Property"
import {PropertyStatus, condoClasses} from "@casas-diamante/types"
import RegionSelector from "@/components/ui/RegionSelector"
import {useRegion} from "@/context/RegionContext"
import AssociatesSelect from "@/components/properties/AssociatesSelect"
import {
   clearPersistedPropertySearchState,
   persistPropertySearchState,
   PropertySearchState,
   readPersistedPropertySearchState,
   usePropertySearchState,
} from "@/hooks/useBackNavigation"
import {parseStoredStringArray, renderSelectedFilterValues} from "@/utils"
import SearchSchemeToggle from "@/components/properties/SearchSchemeToggle"
import type {SelectedScheme} from "@/types/SearchScheme"
import {usePropertySearch} from "@/hooks/usePropertySearch"
import type {PropertySearchFilters, PropertySearchSnapshot} from "@/hooks/usePropertySearch"
import {
   clearPropertySearchCache,
   propertySearchCacheMatchesRegion,
   readPropertySearchCache,
   savePropertySearchCache,
} from "@/utils/propertySearchCache"

interface Associate {
   value: string
   label: string
}

const clearLegacyPropertySearchCache = () => {
   // Old entries cannot safely resume pagination because they lack totalHits and page.
   localStorage.removeItem("properties")
   localStorage.removeItem("filterKey")
}

function Inmuebles() {
   const location = useLocation()
   const restoredRef = useRef(false)
   const pendingRestoreRef = useRef<PropertySearchState | null>(null)
   const [partners, setPartners] = useState<string[]>([])
   const [partnersFetched, setPartnersFetched] = useState<Associate[]>([])
   const [suspended, setSuspended] = useState<string | null>(null)
   const [available, setAvailable] = useState<string | null>(null)
   const [searchString, setSearchString] = useState("")
   const [visibility, setVisibility] = useState<boolean>(false)
   const [status, setStatus] = useState<string[]>([])
   const [condoClass, setCondoClass] = useState<string[]>([])
   const [scheme, setScheme] = useState<SelectedScheme>(null)
   const {user} = useAuthContext()

   const {selectedRegion, setSelectedRegion} = useRegion()

   const storedInitialProperties: Property[] = JSON.parse(localStorage.getItem("initialProperties") || "[]")

   const setStoredSuspended = (val: string | null) => localStorage.setItem("suspended", val || "")
   const storedSuspended = localStorage.getItem("suspended") || ""

   const setStoredAvailable = (val: string | null) => localStorage.setItem("available", val || "")
   const storedAvailable = localStorage.getItem("available") || ""

   const storedSearchQuery = localStorage.getItem("query") || ""
   const setStoredSearchQuery = (val: string) => localStorage.setItem("query", val)

   const storedPartners: string[] = JSON.parse(localStorage.getItem("partners") || "[]")
   const setStoredPartners = (val: string[]) =>
      localStorage.setItem("partners", JSON.stringify(val))

   const fetchedPartners = JSON.parse(localStorage.getItem("fetchedPartners") || "[]")
   const setFetchedPartners = (val: any[]) =>
      localStorage.setItem("fetchedPartners", JSON.stringify(val))

   const storedStatus = parseStoredStringArray(localStorage.getItem("status") || "")
   const setStoredStatus = (val: string[]) => localStorage.setItem("status", JSON.stringify(val))

   const storedCondoClass = parseStoredStringArray(localStorage.getItem("condoClass") || "")
   const setStoredCondoClass = (val: string[]) =>
      localStorage.setItem("condoClass", JSON.stringify(val))

   const storedSchemeValue = localStorage.getItem("scheme")
   const storedScheme: SelectedScheme =
      storedSchemeValue === "sale" || storedSchemeValue === "rent" ? storedSchemeValue : null
   const setStoredScheme = (val: SelectedScheme) => localStorage.setItem("scheme", val || "")

   const {
      inmuebles,
      loading,
      fetchingMore,
      noMoreInm,
      searchPage,
      totalHits,
      hasSearched,
      search,
      fetchMore,
      hydrate,
      reset,
   } = usePropertySearch({
      read: readPropertySearchCache,
      save: savePropertySearchCache,
   })

   const restoreSearch = (snapshot: PropertySearchSnapshot, syncSharedRegion = true) => {
      const {filters} = snapshot

      // Keep the form, visible results, and fetchMore request in sync after restoration.
      if (syncSharedRegion) setSelectedRegion(filters.region)
      setSearchString(filters.searchString)
      setPartners(filters.partners)
      setSuspended(filters.suspended)
      setAvailable(filters.available)
      setStatus(filters.status)
      setCondoClass(filters.condoClass)
      setScheme(filters.scheme)
      hydrate(snapshot)
      setStoredSearchQuery(filters.searchString)
      setStoredPartners(filters.partners)
      setStoredSuspended(filters.suspended)
      setStoredAvailable(filters.available)
      setStoredStatus(filters.status)
      setStoredCondoClass(filters.condoClass)
      setStoredScheme(filters.scheme)
   }

   useEffect(() => {
      const routeRestoredSearch =
         (location.state as {restoredSearch?: PropertySearchState} | null)?.restoredSearch
      const restoredSearch = routeRestoredSearch ?? readPersistedPropertySearchState()

      if (!restoredSearch || restoredRef.current || restoredSearch.source !== "properties") {
         return
      }

      // Route state is an explicit return from a detail page. A background session restore
      // is reused only while its region still matches the app-wide selection.
      if (!routeRestoredSearch && restoredSearch.region && restoredSearch.region !== selectedRegion) {
         clearPersistedPropertySearchState()
         return
      }

      restoredRef.current = true
      restoreSearch({
         properties: restoredSearch.inmuebles,
         page: restoredSearch.searchPage ?? 0,
         totalHits: restoredSearch.totalHits ?? 0,
         noMoreInm: restoredSearch.noMoreInm ?? false,
         filters: {
            region: restoredSearch.region ?? selectedRegion,
            searchString: restoredSearch.searchString ?? "",
            partners: restoredSearch.partners ?? [],
            suspended: restoredSearch.suspended ?? null,
            available: restoredSearch.available ?? null,
            status: parseStoredStringArray(restoredSearch.status),
            condoClass: parseStoredStringArray(restoredSearch.condoClass),
            scheme: restoredSearch.scheme ?? null,
         },
      }, Boolean(routeRestoredSearch))
      pendingRestoreRef.current = restoredSearch
   }, [location.state])

   useEffect(() => {
      const restoredSearch = pendingRestoreRef.current

      if (!restoredSearch || inmuebles.length === 0) return

      const restorePosition = () => {
         const selectedCard = restoredSearch.selectedPropertyId
            ? document.querySelector(`[data-property-id="${restoredSearch.selectedPropertyId}"]`)
            : null

         if (selectedCard instanceof HTMLElement) {
            selectedCard.scrollIntoView({behavior: "auto", block: "center"})
         } else {
            window.scrollTo({top: restoredSearch.scrollY, behavior: "auto"})
         }

         pendingRestoreRef.current = null
      }

      const timeoutId = window.setTimeout(() => {
         requestAnimationFrame(restorePosition)
      }, 0)

      return () => {
         window.clearTimeout(timeoutId)
      }
   }, [inmuebles.length])

   // Fetch partners when the component mounts
   useEffect(() => {
      const fetchPartners = async () => {
         if (fetchedPartners?.length > 0) {
            setPartnersFetched(fetchedPartners)
         }
         const res = await getAssociates()
         const associates: Associate[] = res.map((associate: any) => ({
            value: associate.id,
            label: associate.name,
         }))
         setPartnersFetched(associates)
         setFetchedPartners(associates)
      }

      const dataCache = async () => {
         if (restoredRef.current) return
         const cachedSearch = readPropertySearchCache()

         if (cachedSearch) {
            if (propertySearchCacheMatchesRegion(cachedSearch, selectedRegion)) {
               // A complete snapshot takes precedence over independently stored draft fields.
               restoreSearch(cachedSearch, false)
               return
            }

            // The shared region may have changed elsewhere; do not restore results for another region.
            clearPropertySearchCache()
         }

         clearLegacyPropertySearchCache()
         if (storedSearchQuery) setSearchString(storedSearchQuery)
         if (storedPartners?.length > 0) setPartners(storedPartners)
         if (storedSuspended) setSuspended(storedSuspended)
         if (storedAvailable) setAvailable(storedAvailable)
         if (storedStatus.length > 0) setStatus(storedStatus)
         if (storedCondoClass.length > 0) setCondoClass(storedCondoClass)
         if (storedScheme) setScheme(storedScheme)
      }
      fetchPartners().then(() => dataCache())
   }, [])

   const propertySearchState = usePropertySearchState({
      source: "properties",
      returnTo: "/inmuebles",
      inmuebles,
      searchString,
      partners,
      suspended,
      available,
      searchPage,
      totalHits,
      noMoreInm,
      region: selectedRegion,
      status,
      condoClass,
      scheme,
   })

   useEffect(() => {
      if (!hasSearched) return

      persistPropertySearchState({
         ...propertySearchState,
         scrollY: window.scrollY,
      })
   }, [propertySearchState, hasSearched, inmuebles.length])

   useEffect(() => {
      if (!hasSearched) return

      const saveScrollPosition = () => {
         persistPropertySearchState({
            ...propertySearchState,
            scrollY: window.scrollY,
         })
      }

      window.addEventListener("scroll", saveScrollPosition, {passive: true})

      return () => {
         window.removeEventListener("scroll", saveScrollPosition)
      }
   }, [propertySearchState, hasSearched, inmuebles.length])

   // Handler for search string changes
   const handleSearchStringChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchString(e.target.value)
      setStoredSearchQuery(e.target.value)
   }

   const getSearchFilters = (schemeToSearch: SelectedScheme = scheme): PropertySearchFilters => ({
      region: selectedRegion,
      searchString,
      partners,
      suspended,
      available,
      status,
      condoClass,
      scheme: schemeToSearch,
   })

   const handleSearch = (e?: React.FormEvent) => {
      e?.preventDefault()
      void search(getSearchFilters())
   }

   // Handler for clearing all search parameters
   const clearSearchParams = () => {
      setPartners([])
      setStoredPartners([])
      setSuspended(null)
      setStoredSuspended(null)
      setAvailable(null)
      setStoredAvailable(null)
      setSearchString("")
      setStoredSearchQuery("")
      setStatus([])
      setStoredStatus([])
      setCondoClass([])
      setStoredCondoClass([])
      setScheme(null)
      setStoredScheme(null)
      clearPropertySearchCache()
      clearLegacyPropertySearchCache()
      clearPersistedPropertySearchState()
      reset(storedInitialProperties)
   }

   const handleFilterChange =
      (key: "suspended" | "available") => (event: SelectChangeEvent<any>) => {
         const value = event.target.value

         if (key === "suspended") {
            const newValue = value === suspended ? null : value
            setSuspended(newValue)
            setStoredSuspended(newValue)
         } else if (key === "available") {
            const newValue = value === available ? null : value
            setAvailable(newValue)
            setStoredAvailable(newValue)
         }
      }

   const handleStatusChange = (event: SelectChangeEvent<string[]>) => {
      const value = event.target.value
      const newValue = typeof value === "string" ? value.split(",") : value
      setStatus(newValue)
      setStoredStatus(newValue)
   }

   const handleCondoClassChange = (event: SelectChangeEvent<string[]>) => {
      const value = event.target.value
      const newValue = typeof value === "string" ? value.split(",") : value
      setCondoClass(newValue)
      setStoredCondoClass(newValue)
   }

   const handleChangePartners = (event: SelectChangeEvent<(string | number)[]>) => {
      const {
         target: {value},
      } = event
      setPartners(typeof value === "string" ? value.split(",") : value.map(String))
   }

   const handleSchemeChange = (nextScheme: SelectedScheme) => {
      setScheme(nextScheme)
      setStoredScheme(nextScheme)
      void search(getSearchFilters(nextScheme))
   }

   return (
      <Page className="flex flex-col items-center my-8 gap-4 p-4 justify-center">
         <h1 className="text-2xl md:text-3xl font-bold text-blue text-center">
            Buscador de Propiedades Socios
         </h1>
         <section className="flex flex-col md:flex-row gap-4 md:gap-8">
            {user.admin && (
               <>
                  <Link to="nuevo" className="flex py-2 px-4 gap-2 rounded-full bg-blue text-white">
                     <AddIcon />
                     Agregar Inmueble
                  </Link>
                  <button
                     onClick={() => setVisibility(true)}
                     className="flex py-2 px-4 gap-2 rounded-full bg-blue text-white"
                  >
                     Ver modo Excel
                     <ListAltIcon />
                  </button>
               </>
            )}
         </section>
         <div className="grid grid-cols-1 md:grid-cols-2 gap-1 w-auto justify-center items-center">
            <RegionSelector />
            {user.admin && (
               <AssociatesSelect
                  value={partners}
                  onChange={handleChangePartners}
                  options={partnersFetched}
               />
            )}
            <FormControl className="w-[250px]" color="primary" size="small">
               <InputLabel>Disponibilidad</InputLabel>
               <Select
                  value={available || ""}
                  onChange={handleFilterChange("available")}
                  input={<OutlinedInput label="Disponibilidad" />}
                  renderValue={(selected) => (
                     <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                        {selected && (
                           <Chip
                              key={selected}
                              label={selected === "true" ? "Disponible" : "No Disponible"}
                              sx={{bgcolor: CDBLUE, color: "white"}}
                           />
                        )}
                     </Box>
                  )}
               >
                  <MenuItem
                     value="true"
                     onClick={() => setAvailable((prev) => (prev === "true" ? null : "true"))}
                     selected={available === "true"}
                  >
                     Disponible
                  </MenuItem>
                  <MenuItem
                     value="false"
                     onClick={() => setAvailable((prev) => (prev === "true" ? null : "true"))}
                     selected={available === "false"}
                  >
                     No Disponible
                  </MenuItem>
               </Select>
            </FormControl>
            <FormControl className="w-[250px]" color="primary" size="small">
               <InputLabel>Visibilidad</InputLabel>
               <Select
                  value={suspended || ""}
                  onChange={handleFilterChange("suspended")}
                  input={<OutlinedInput label="Visibilidad" />}
                  renderValue={(selected) => (
                     <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                        {selected && (
                           <Chip
                              key={selected}
                              label={selected === "true" ? "Apagado" : "Encendido"}
                              sx={{bgcolor: CDBLUE, color: "white"}}
                           />
                        )}
                     </Box>
                  )}
               >
                  <MenuItem
                     value="false"
                     onClick={() => setSuspended((prev) => (prev === "false" ? null : "false"))}
                     selected={suspended === "false"}
                  >
                     Encendido
                  </MenuItem>
                  <MenuItem
                     value="true"
                     onClick={() => setSuspended((prev) => (prev === "true" ? null : "true"))}
                     selected={suspended === "true"}
                  >
                     Apagado
                  </MenuItem>
               </Select>
            </FormControl>
            <FormControl className="w-[250px]" color="primary" size="small">
               <InputLabel>Estatus del inmueble</InputLabel>
               <Select
                  multiple
                  value={status}
                  onChange={handleStatusChange}
                  input={<OutlinedInput label="Clasificación" />}
                  renderValue={renderSelectedFilterValues}
               >
                  {Object.values(PropertyStatus).map((s) => (
                     <MenuItem key={s} value={s} selected={status.includes(s)}>
                        {s}
                     </MenuItem>
                  ))}
               </Select>
            </FormControl>
            <FormControl className="w-[250px]" color="primary" size="small">
               <InputLabel>Clasificación del condominio</InputLabel>
               <Select
                  multiple
                  value={condoClass}
                  onChange={handleCondoClassChange}
                  input={<OutlinedInput label="Clasificación del condominio" />}
                  renderValue={renderSelectedFilterValues}
               >
                  {condoClasses.map((c) => (
                     <MenuItem key={c} value={c} selected={condoClass.includes(c)}>
                        {c}
                     </MenuItem>
                  ))}
               </Select>
            </FormControl>
         </div>
         <div>
            <SearchSchemeToggle value={scheme} onChange={handleSchemeChange} />
         </div>
         <form className="flex flex-col items-center content-center gap-4" action="submit">
            <div className="flex border border-blue rounded-md w-full px-2 py-1 items-center">
               <input
                  type="text"
                  name="Search String"
                  id="search-string"
                  value={searchString}
                  onChange={handleSearchStringChange}
                  className="px-2 py-1 focus:outline-none"
                  placeholder="Buscar por nombre"
               />
            </div>
            <div className="flex items-center gap-2">
               <button
                  onClick={handleSearch}
                  type="submit"
                  className="border rounded-[10px] bg-blue text-white px-2 py-1"
               >
                  Buscar
               </button>
               <button
                  onClick={clearSearchParams}
                  type="button"
                  className="border rounded-[10px] bg-red text-white px-2 py-1"
               >
                  Restablecer búsqueda
               </button>
            </div>
         </form>
         <div>
            {/* Intial text to start searching property */}
            {!loading && inmuebles.length === 0 && (
               <h2 className="text-center text-xl font-bold mt-20">
                  {hasSearched && totalHits !== 0
                     ? "No se encontraron resultados"
                     : "Busca una propiedad"}
               </h2>
            )}
         </div>
         {loading ? (
            <h2 className="text-center text-xl font-bold mt-20 animate-pulse">Cargando...</h2>
         ) : (
            <div>
               {!loading && totalHits > 0 && (
                  <h4 className="text-center font-bold my-4">
                     {totalHits} resultado{totalHits > 1 && "s"} para la búsqueda.
                  </h4>
               )}
               <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 items-center justify-center gap-8 mx-auto">
                  {inmuebles.map((inmueble) => (
                     <InmuebleCard
                        key={inmueble.id}
                        property={inmueble}
                        searchState={propertySearchState}
                     />
                  ))}
               </section>
            </div>
         )}
         <section className="my-8 flex flex-col gap-4">
            {fetchingMore ? (
               <h2 className="text-center text-xl font-bold mt-20 animate-pulse">
                  Cargando más...
               </h2>
            ) : (
               !loading &&
               !noMoreInm &&
               // A partial final page must still allow loading more when the server reports more hits.
               inmuebles.length < totalHits && (
                  <button
                     onClick={fetchMore}
                     disabled={loading || fetchingMore}
                     className="border rounded-[10px] bg-green text-white px-2 py-1"
                  >
                     Cargar más
                  </button>
               )
            )}
            <button
               onClick={() => window.scrollTo({top: 0, behavior: "smooth"})}
               className="border rounded-[10px] bg-green text-white px-2 py-1 flex items-center gap-2"
            >
               Volver arriba
               <ArrowUpwardIcon />
            </button>
         </section>
         {visibility && <CSVPopUpWindow setVisibility={setVisibility} />}
      </Page>
   )
}

export default Inmuebles
